import React, { useEffect, useState } from 'react';
import {
    FaTimes,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaSearch,
    FaListUl,
    FaTruck,
    FaChevronDown,
    FaBox,
    FaRupeeSign,
    FaUser,
    FaClock,
    FaSpinner,
    FaCheckCircle,
    FaDownload,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMyContext } from '../../../context/MyContext';
import { Base_Url, b2bAllShipmentList, b2bTrackShipment, b2bLabel } from '../../../config/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TOAST_CONTAINER_ID = 'b2b-tracking-popup-toast';

// =========================
// Status Color
// =========================
const statusColor = (status) => {
    const s = (status || '').toLowerCase();

    if (s.includes('deliver')) return 'bg-emerald-500';
    if (s.includes('transit')) return 'bg-blue-500';
    if (s.includes('out for delivery')) return 'bg-orange-500';

    if (
        s.includes('fail') ||
        s.includes('exception') ||
        s.includes('cancel')
    ) {
        return 'bg-red-500';
    }

    return 'bg-gray-400';
};

// Check if status is delivered
const isDeliveredStatus = (status) => {
    const s = (status || '').toLowerCase();
    return s.includes('delivered') || s.includes('completed');
};

// =========================
// Robust Scans Extractor
// =========================
const extractScans = (detail) => {
    if (!detail) return [];

    if (Array.isArray(detail?.Scans) && detail.Scans.length > 0) {
        return detail.Scans;
    }

    const possibleArrays = [
        detail?.Scan,
        detail?.ScanDetail,
        detail?.scans,
        detail?.scan,
        detail?.TrackingHistory,
        detail?.tracking_history,
        detail?.Status?.Scans,
    ];

    for (const arr of possibleArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
            return arr.map((item) => {
                if (item?.ScanDetail) return item;
                return {
                    ScanDetail: {
                        Instructions:
                            item?.Instructions ||
                            item?.Remark ||
                            item?.Status ||
                            item?.status ||
                            item?.scan_remark ||
                            'Shipment update',
                        StatusDateTime:
                            item?.StatusDateTime ||
                            item?.DateTime ||
                            item?.date ||
                            item?.created_at,
                        ScannedLocation:
                            item?.ScannedLocation ||
                            item?.Location ||
                            item?.location,
                        Scan: item?.Scan || item?.ScanType || item?.status,
                    },
                };
            });
        }
    }

    if (detail?.Status?.Status || detail?.Status?.StatusLocation) {
        return [
            {
                ScanDetail: {
                    Instructions:
                        detail?.Status?.Instructions ||
                        detail?.Status?.Status ||
                        detail?.Status?.StatusLocation,
                    StatusDateTime: detail?.Status?.StatusDateTime,
                    ScannedLocation:
                        detail?.Destination || detail?.Origin || '',
                    Scan: detail?.Status?.Status,
                },
            },
        ];
    }

    return [];
};

// =========================
// Skeleton Card
// =========================
const SkeletonCard = ({ isDarkMode }) => (
    <div
        className={`
      rounded-[20px]
      border
      p-4
      animate-pulse
      backdrop-blur-2xl
      shadow-[0_8px_30px_rgba(0,0,0,0.04)]
      ${isDarkMode
                ? 'bg-white/[0.08] border-white/10'
                : 'bg-white/50 border-white/70'
            }
    `}
    >
        <div className="flex items-center justify-between mb-3">
            <div
                className={`h-4 w-28 rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                    }`}
            />

            <div
                className={`h-5 w-16 rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                    }`}
            />
        </div>

        <div
            className={`h-3 w-32 rounded-full mb-2 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />

        <div
            className={`h-3 w-20 rounded-full mb-4 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />

        <div
            className={`h-9 w-full rounded-xl ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />
    </div>
);

// =========================
// Accordion Detail Row
// =========================
const DetailRow = ({ label, value, icon: Icon, isDarkMode }) => (
    <div
        className={`
      flex items-center justify-between
      py-2.5
      border-b last:border-b-0
      ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
    `}
    >
        <div className="flex items-center gap-2">
            {Icon && (
                <div
                    className={`
            w-6 h-6 rounded-lg
            flex items-center justify-center flex-shrink-0
            ${isDarkMode ? 'bg-white/10' : 'bg-black/[0.05]'}
          `}
                >
                    <Icon
                        className={`w-3 h-3 ${isDarkMode ? 'text-white/40' : 'text-black/40'
                            }`}
                    />
                </div>
            )}
            <span
                className={`text-[10.5px] font-medium ${isDarkMode ? 'text-white/50' : 'text-black/50'
                    }`}
            >
                {label}
            </span>
        </div>
        <span
            className={`text-[11px] font-semibold text-right ${isDarkMode ? 'text-white' : 'text-black'
                }`}
        >
            {value || 'Not Available'}
        </span>
    </div>
);

// =========================
// Accordion Section
// =========================
const AccordionSection = ({
    icon: Icon,
    title,
    isOpen,
    onToggle,
    isDarkMode,
    children,
}) => (
    <div
        className={`
      rounded-[16px]
      border
      overflow-hidden
      transition-all
      duration-300
      ${isDarkMode
                ? 'bg-white/[0.05] border-white/10'
                : 'bg-white border-black/[0.06]'
            }
    `}
    >
        <button
            onClick={onToggle}
            className={`
        w-full
        flex items-center justify-between
        px-3.5 py-3
      `}
        >
            <div className="flex items-center gap-2">
                <div
                    className={`
            w-6 h-6 rounded-lg
            flex items-center justify-center flex-shrink-0
            ${isDarkMode ? 'bg-white/10' : 'bg-black/[0.06]'}
          `}
                >
                    <Icon
                        className={`w-3 h-3 ${isDarkMode ? 'text-white' : 'text-black'
                            }`}
                    />
                </div>
                <span
                    className={`text-[11.5px] font-bold ${isDarkMode ? 'text-white' : 'text-black'
                        }`}
                >
                    {title}
                </span>
            </div>

            <FaChevronDown
                size={10}
                className={`
          transition-transform duration-300
          ${isOpen ? 'rotate-180' : 'rotate-0'}
          ${isDarkMode ? 'text-white/40' : 'text-black/35'}
        `}
            />
        </button>

        <div
            className={`
        grid transition-all duration-300 ease-in-out
        ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
      `}
        >
            <div className="overflow-hidden">
                <div className="px-3.5 pb-3">{children}</div>
            </div>
        </div>
    </div>
);

// =========================
// B2B Tracking Side Popup
// =========================
const B2BTrackingSidePopup = ({
    isOpen,
    onClose,
    searchTerm,
    setSearchTerm,
    isDarkMode,
}) => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState('');

    const [openSection, setOpenSection] = useState('overview');

    const [directSearchLoading, setDirectSearchLoading] = useState(false);
    const [directSearchResult, setDirectSearchResult] = useState(null);
    const [directSearchError, setDirectSearchError] = useState('');

    const [downloadingPOD, setDownloadingPOD] = useState(null);

    const { setorderId } = useMyContext();
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const cusId = localStorage.getItem('cus_id');

    // =========================
    // Fetch Shipments (B2B list)
    // =========================
    useEffect(() => {
        if (isOpen) {
            fetchShipments();
        }

        if (!isOpen) {
            setExpandedOrderId(null);
            setDetail(null);
            setDetailError('');
            setDirectSearchResult(null);
            setDirectSearchError('');
        }
    }, [isOpen]);

    const fetchShipments = async () => {
        setLoading(true);

        try {
            const res = await fetch(
                `${Base_Url}${b2bAllShipmentList}?page=1&limit=15`,
                {
                    method: 'GET',
                    headers: {
                        token,
                        cus_id: cusId,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = await res.json();

            setShipments(
                Array.isArray(data?.data) ? data.data.slice(0, 15) : []
            );
        } catch (err) {
            console.error('Error fetching B2B shipments:', err);
            setShipments([]);
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // 🔥 Download POD Function (matches B2bAllShipmentList)
    // Calls the getEpod API live for that order_id:
    // GET B2B/shipment/getEpod/{order_id}
    // On success it opens data.data.files[0].url (S3 link already has
    // response-content-disposition=attachment, so window.open triggers a download).
    // =========================
    const handleDownloadPOD = async (orderId, e) => {
        if (e) e.stopPropagation();

        setDownloadingPOD(orderId);

        try {
            const response = await fetch(`${Base_Url}B2B/shipment/getEpod/${orderId}`, {
                method: 'GET',
                headers: {
                    token,
                    cus_id: cusId,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            const fileUrl = data?.data?.files?.[0]?.url;

            if (data?.status === 'SUCCESS' && fileUrl) {
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
                toast.success('POD downloaded successfully!', {
                    containerId: TOAST_CONTAINER_ID,
                    position: 'top-right',
                    autoClose: 2000,
                });
            } else {
                toast.error(
                    data?.message || 'POD not available for this shipment',
                    {
                        containerId: TOAST_CONTAINER_ID,
                        position: 'top-right',
                        autoClose: 2000,
                    }
                );
            }
        } catch (error) {
            console.error('POD download error:', error);
            toast.error('Failed to fetch POD', {
                containerId: TOAST_CONTAINER_ID,
                position: 'top-right',
                autoClose: 2000,
            });
        } finally {
            setDownloadingPOD(null);
        }
    };

    // =========================
    // Search
    // =========================
    const filtered = shipments.filter((s) => {
        if (!searchTerm?.trim()) return true;

        const term = searchTerm.toLowerCase();

        return (
            s?.order_id?.toString().toLowerCase().includes(term) ||
            s?.waybill?.toString().toLowerCase().includes(term) ||
            s?.lrn?.toString().toLowerCase().includes(term)
        );
    });

    // =========================
    // Direct search fallback
    // =========================
    useEffect(() => {
        const term = searchTerm?.trim();

        setDirectSearchResult(null);
        setDirectSearchError('');

        if (!isOpen || !term || filtered.length > 0) return;

        const timer = setTimeout(async () => {
            setDirectSearchLoading(true);
            try {
                const res = await axios.post(
                    Base_Url + b2bTrackShipment,
                    { order_id: term, shipment_type: 'Forward' },
                    {
                        headers: {
                            token,
                            cus_id: cusId,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const shipment = res?.data?.data?.ShipmentData?.[0]?.Shipment;

                if (res?.data?.status === 'SUCCESS' && shipment) {
                    setDirectSearchResult(shipment);
                } else {
                    setDirectSearchError(
                        res?.data?.message || 'No shipment found for this ID'
                    );
                }
            } catch (err) {
                console.error('Direct search tracking error:', err);
                setDirectSearchError('Failed to fetch tracking information');
            } finally {
                setDirectSearchLoading(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm, filtered.length, isOpen]);

    const syntheticShipment = directSearchResult
        ? {
            order_id: searchTerm.trim(),
            waybill: directSearchResult?.AWB || searchTerm.trim(),
            status: directSearchResult?.Status?.Status,
            pickup_city: directSearchResult?.Origin,
            city: directSearchResult?.Destination,
            created_at: directSearchResult?.PickUpDate,
            epod_file: directSearchResult?.EpodFile || directSearchResult?.epod_file,
        }
        : null;

    const displayList = syntheticShipment
        ? [syntheticShipment, ...filtered]
        : filtered;

    // =========================
    // Toggle View Details
    // =========================
    const handleToggleDetails = async (orderId) => {
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
            setDetail(null);
            setDetailError('');
            return;
        }

        if (syntheticShipment && orderId === syntheticShipment.order_id) {
            setorderId(orderId);
            setExpandedOrderId(orderId);
            setDetail(directSearchResult);
            setDetailError('');
            setOpenSection('status');
            return;
        }

        setorderId(orderId);
        setExpandedOrderId(orderId);
        setDetail(null);
        setDetailError('');
        setOpenSection('overview');
        setDetailLoading(true);

        try {
            const res = await axios.post(
                Base_Url + b2bTrackShipment,
                {
                    order_id: orderId,
                    shipment_type: 'Forward',
                },
                {
                    headers: {
                        token,
                        cus_id: cusId,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const shipment = res?.data?.data?.ShipmentData?.[0]?.Shipment;

            if (res?.data?.status === 'SUCCESS' && shipment) {
                setDetail(shipment);
            } else {
                setDetailError(
                    res?.data?.message || 'Unable to fetch tracking details'
                );
            }
        } catch (err) {
            console.error('Tracking error:', err);
            setDetailError('Failed to fetch tracking information');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleToggleSection = (key) => {
        setOpenSection((prev) => (prev === key ? null : key));
    };

    const handleViewAll = () => {
        onClose();
        navigate('/b2b/shipment/all');
    };

    const formatDate = (date) => {
        if (!date) return 'Not Available';

        try {
            return new Date(date).toLocaleString('en-IN');
        } catch {
            return 'Not Available';
        }
    };

    if (!isOpen) return null;

    const scans = extractScans(detail);

    return (
        <>
            {/* Toast Container for this popup */}
            <ToastContainer
                containerId={TOAST_CONTAINER_ID}
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <style>
                {`
          @keyframes trackingPopupIn {
            0% {
              transform: translateX(110%);
              opacity: 0;
            }

            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .tracking-popup-enter {
            animation: trackingPopupIn
              0.5s
              cubic-bezier(0.22, 1, 0.36, 1)
              forwards;
          }

          .tracking-popup-scroll::-webkit-scrollbar {
            width: 4px;
          }

          .tracking-popup-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .tracking-popup-scroll::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.12);
            border-radius: 999px;
          }

          .tracking-popup-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.22);
          }

          .tracking-popup-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(0,0,0,0.12) transparent;
          }
        `}
            </style>

            <div
                className="
          fixed
          inset-0
          z-[9998]
          bg-black/20
          backdrop-blur-[6px]
          animate-[fadeIn_0.3s_ease-out]
        "
                onClick={onClose}
            />

            <div
                className={`
          tracking-popup-enter

          fixed
          top-3
          right-3
          bottom-3

          w-[min(330px,70vw)]

          z-[9999]
          overflow-hidden

          rounded-[28px]

          border
          border-white/70

          shadow-[0_25px_80px_rgba(0,0,0,0.18)]

          backdrop-blur-[45px]

          flex
          flex-col

          ${isDarkMode
                        ? `
                bg-slate-900/80
                text-white
              `
                        : `
                bg-gradient-to-br
                from-white/80
                via-slate-100/70
                to-blue-100/60
                text-black
              `
                    }
        `}
            >
                <div
                    className="
            pointer-events-none
            absolute
            -top-24
            -right-20
            w-56
            h-56
            rounded-full
            bg-blue-300/20
            blur-[70px]
          "
                />

                <div
                    className="
            pointer-events-none
            absolute
            top-[35%]
            -left-24
            w-52
            h-52
            rounded-full
            bg-purple-300/15
            blur-[70px]
          "
                />

                <div
                    className="
            pointer-events-none
            absolute
            bottom-[-80px]
            right-[-50px]
            w-64
            h-64
            rounded-full
            bg-cyan-300/15
            blur-[80px]
          "
                />

                <div
                    className={`
            relative
            z-10

            flex
            items-center
            justify-between

            px-5
            py-4

            border-b

            ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
          `}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className={`
                w-9
                h-9
                rounded-[13px]

                flex
                items-center
                justify-center

                border
                shadow-sm

                ${isDarkMode
                                    ? 'bg-white/10 border-white/10'
                                    : 'bg-white/65 border-white/80'
                                }
              `}
                        >
                            <FaTruck
                                size={15}
                                className={isDarkMode ? 'text-white' : 'text-black'}
                            />
                        </div>

                        <p
                            className={`
                text-[16px]
                font-semibold

                ${isDarkMode ? 'text-white/60' : 'text-black/55'}
              `}
                        >
                            Track your shipments
                        </p>
                    </div>

                    <button
                        onClick={onClose}
                        className={`
              w-8
              h-8

              flex
              items-center
              justify-center

              rounded-full
              border

              transition-all
              duration-200

              ${isDarkMode
                                ? `
                    bg-white/10
                    border-white/10
                    text-white
                    hover:bg-white/20
                  `
                                : `
                    bg-white/60
                    border-white/80
                    text-black
                    hover:bg-white/90
                  `
                            }
            `}
                    >
                        <FaTimes size={13} />
                    </button>
                </div>

                <div className="relative z-10 px-5 py-3.5">
                    <div className="relative">
                        <FaSearch color='blue'
                            className={`
                absolute
                left-3.5
                top-1/2
                -translate-y-1/2

                ${isDarkMode ? 'text-white/40' : ''}
              `}
                            size={12}
                        />

                        <input
                            autoFocus
                            type="text"
                            value={searchTerm || ''}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by Order ID or LR Number..."
                            className={`
                w-full

                pl-9
                pr-4
                py-2.5

                rounded-[14px]

                text-[12px]
                font-medium

                outline-none
                border

                backdrop-blur-xl

                transition-all
                duration-200

                ${isDarkMode
                                    ? `
                      bg-white/10
                      border-white/10
                      text-white
                      placeholder:text-white/35
                      focus:bg-white/15
                    `
                                    : `
                      bg-white/60
                      border-white/80
                      text-black
                      placeholder:text-black/35
                      focus:bg-white/80
                    `
                                }
              `}
                        />
                    </div>
                </div>

                <div
                    className="
            relative
            z-10
            flex-1

            overflow-y-auto
            tracking-popup-scroll

            px-5
            pb-4

            space-y-2.5
          "
                >
                    {loading && (
                        <>
                            <SkeletonCard isDarkMode={isDarkMode} />
                            <SkeletonCard isDarkMode={isDarkMode} />
                            <SkeletonCard isDarkMode={isDarkMode} />
                        </>
                    )}

                    {!loading && directSearchLoading && (
                        <SkeletonCard isDarkMode={isDarkMode} />
                    )}

                    {!loading &&
                        !directSearchLoading &&
                        displayList.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div
                                    className={`
                  w-12
                  h-12
                  rounded-full

                  flex
                  items-center
                  justify-center

                  mb-3
                  border

                  ${isDarkMode
                                            ? 'bg-white/10 border-white/10'
                                            : 'bg-white/60 border-white/80'
                                        }
                `}
                                >
                                    <FaSearch
                                        size={14}
                                        className={
                                            isDarkMode ? 'text-white/40' : 'text-black/30'
                                        }
                                    />
                                </div>

                                <p
                                    className={`
                  text-[11px]
                  font-semibold

                  ${isDarkMode ? 'text-white/60' : 'text-black/55'}
                `}
                                >
                                    {searchTerm?.trim()
                                        ? directSearchError || 'No shipments found'
                                        : 'No shipments found'}
                                </p>
                            </div>
                        )}

                    {!loading &&
                        !directSearchLoading &&
                        displayList.map((s, index) => {
                            const orderId = s?.order_id;
                            const isExpanded = expandedOrderId === orderId;
                            const status = s?.status || 'Pending';
                            const delivered = isDeliveredStatus(status);

                            return (
                                <div key={orderId || s?.waybill || index}>
                                    <div
                                        className={`
                      group

                      rounded-[19px]

                      p-3.5

                      border

                      backdrop-blur-2xl

                      transition-all
                      duration-300

                      shadow-[0_8px_30px_rgba(0,0,0,0.04)]

                      ${isDarkMode
                                                ? `
                            bg-white/[0.08]
                            border-white/10
                            hover:bg-white/[0.12]
                          `
                                                : `
                            bg-white/55
                            border-white/80
                            hover:bg-white/75
                          `
                                            }
                    `}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span
                                                className={`
                          font-bold
                          text-[12px]
                          tracking-[-0.01em]

                          ${isDarkMode ? 'text-white' : 'text-black'}
                        `}
                                            >
                                                {s?.waybill || s?.lrn || s?.order_id || 'N/A'}
                                            </span>

                                            {/* Status Badge + Download POD button for delivered */}
                                            <div className="flex items-center gap-1.5">
                                                <span
                                                    className={`
                          text-[8px]

                          px-2
                          py-1

                          rounded-full

                          text-white
                          font-bold
                          tracking-wide

                          shadow-sm

                          ${statusColor(status)}
                        `}
                                                >
                                                    {status}
                                                </span>

                                                {delivered && (
                                                    <button
                                                        onClick={(e) => handleDownloadPOD(orderId, e)}
                                                        disabled={downloadingPOD === orderId}
                                                        className={`
                                                            w-6 h-6
                                                            rounded-full
                                                            flex items-center justify-center
                                                            transition-all duration-200
                                                            border
                                                            ${isDarkMode
                                                                ? 'bg-white/10 border-white/10 text-white hover:bg-white/20'
                                                                : 'bg-white/60 border-white/80 text-black hover:bg-white/90'
                                                            }
                                                            ${downloadingPOD === orderId ? 'opacity-50 cursor-not-allowed' : ''}
                                                        `}
                                                        title="Download POD"
                                                    >
                                                        {downloadingPOD === orderId ? (
                                                            <FaSpinner size={10} className="animate-spin" />
                                                        ) : (
                                                            <FaDownload size={10} className="text-emerald-500" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div
                                            className={`
                        flex
                        items-center
                        gap-2

                        text-[10px]
                        font-medium

                        mb-1.5

                        ${isDarkMode ? 'text-white/65' : 'text-black/60'}
                      `}
                                        >
                                            <FaMapMarkerAlt
                                                size={9}
                                                className={
                                                    isDarkMode ? 'text-white/40' : 'text-black/35'
                                                }
                                            />

                                            <span>
                                                {s?.pickup_city || '—'} → {s?.city || '—'}
                                            </span>
                                        </div>

                                        <div
                                            className={`
                        flex
                        items-center
                        gap-2

                        text-[9px]
                        font-medium

                        mb-3

                        ${isDarkMode ? 'text-white/40' : 'text-black/40'}
                      `}
                                        >
                                            <FaCalendarAlt size={9} />

                                            <span>
                                                {s?.created_at
                                                    ? new Date(s.created_at).toLocaleDateString(
                                                        'en-IN'
                                                    )
                                                    : '—'}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handleToggleDetails(orderId)}
                                            className={`
                        w-full

                        flex
                        items-center
                        justify-center
                        gap-1.5

                        py-2.5

                        rounded-[13px]

                        text-[10px]
                        font-bold

                        border

                        transition-all
                        duration-200

                        ${isDarkMode
                                                    ? `
                              bg-white/10
                              border-white/10
                              text-white
                              hover:bg-white/20
                            `
                                                    : `
                              bg-black/[0.04]
                              border-black/[0.06]
                              text-black
                              hover:bg-black/[0.08]
                            `
                                                }
                      `}
                                        >
                                            {isExpanded && detailLoading ? (
                                                <FaSpinner size={10} className="animate-spin" />
                                            ) : (
                                                <FaChevronDown
                                                    size={9}
                                                    className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''
                                                        }`}
                                                />
                                            )}
                                            {isExpanded ? 'Hide Details' : 'View Details'}
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="mt-2.5 space-y-2.5 animate-[fadeIn_0.25s_ease-out]">
                                            {detailLoading && (
                                                <SkeletonCard isDarkMode={isDarkMode} />
                                            )}

                                            {!detailLoading && detailError && (
                                                <div
                                                    className={`
                            p-3
                            rounded-[16px]
                            border
                            text-center
                            text-[10.5px]
                            font-semibold
                            text-red-500
                            ${isDarkMode
                                                            ? 'bg-red-500/10 border-red-500/20'
                                                            : 'bg-red-50 border-red-100'
                                                        }
                          `}
                                                >
                                                    {detailError}
                                                </div>
                                            )}

                                            {!detailLoading && detail && (
                                                <>
                                                    <AccordionSection
                                                        icon={FaClock}
                                                        title="Current Status"
                                                        isOpen={openSection === 'status'}
                                                        onToggle={() =>
                                                            handleToggleSection('status')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <div className="mb-3">
                                                            <p
                                                                className={`text-[11px] font-bold mb-0.5 ${isDarkMode
                                                                        ? 'text-white'
                                                                        : 'text-black'
                                                                    }`}
                                                            >
                                                                {detail?.Status?.Status ||
                                                                    'Pending'}
                                                            </p>
                                                            <p
                                                                className={`text-[9.5px] leading-relaxed ${isDarkMode
                                                                        ? 'text-white/50'
                                                                        : 'text-black/50'
                                                                    }`}
                                                            >
                                                                {detail?.Status?.Instructions ||
                                                                    'No instructions available'}
                                                            </p>
                                                            <p
                                                                className={`text-[8px] mt-1 ${isDarkMode
                                                                        ? 'text-white/30'
                                                                        : 'text-black/30'
                                                                    }`}
                                                            >
                                                                Updated:{' '}
                                                                {formatDate(
                                                                    detail?.Status
                                                                        ?.StatusDateTime
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div
                                                            className={`pt-2 border-t ${isDarkMode
                                                                    ? 'border-white/10'
                                                                    : 'border-black/[0.06]'
                                                                }`}
                                                        >
                                                            <p
                                                                className={`text-[8px] font-bold uppercase tracking-wider mb-2.5 ${isDarkMode
                                                                        ? 'text-white/35'
                                                                        : 'text-black/35'
                                                                    }`}
                                                            >
                                                                Tracking Flow
                                                            </p>

                                                            {scans.length > 0 ? (
                                                                <div>
                                                                    {scans.map((scan, sIndex) => (
                                                                        <div
                                                                            key={sIndex}
                                                                            className="relative pb-4 last:pb-0"
                                                                        >
                                                                            <div className="flex items-start gap-2.5">
                                                                                <div className="relative z-10 flex-shrink-0">
                                                                                    <div
                                                                                        className={`
                                    w-6 h-6 rounded-full
                                    flex items-center justify-center
                                    border
                                    ${sIndex === 0
                                                                                                ? 'bg-black text-white border-black'
                                                                                                : isDarkMode
                                                                                                    ? 'bg-white/10 text-white/50 border-white/10'
                                                                                                    : 'bg-white text-black/35 border-black/10'
                                                                                            }
                                  `}
                                                                                    >
                                                                                        {sIndex === 0 ? (
                                                                                            <FaTruck size={9} />
                                                                                        ) : (
                                                                                            <FaCheckCircle size={9} />
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                <div className="flex-1 pt-0.5">
                                                                                    <p
                                                                                        className={`text-[10px] font-bold leading-relaxed ${isDarkMode
                                                                                                ? 'text-white'
                                                                                                : 'text-black'
                                                                                            }`}
                                                                                    >
                                                                                        {scan?.ScanDetail
                                                                                            ?.Instructions ||
                                                                                            scan?.ScanDetail?.Scan ||
                                                                                            'Shipment update'}
                                                                                    </p>
                                                                                    <p
                                                                                        className={`text-[8px] mt-1 ${isDarkMode
                                                                                                ? 'text-white/35'
                                                                                                : 'text-black/35'
                                                                                            }`}
                                                                                    >
                                                                                        {formatDate(
                                                                                            scan?.ScanDetail
                                                                                                ?.StatusDateTime
                                                                                        )}
                                                                                    </p>
                                                                                    <p
                                                                                        className={`text-[8px] mt-0.5 ${isDarkMode
                                                                                                ? 'text-white/35'
                                                                                                : 'text-black/35'
                                                                                            }`}
                                                                                    >
                                                                                        {scan?.ScanDetail
                                                                                            ?.ScannedLocation ||
                                                                                            'Location unavailable'}
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            {sIndex < scans.length - 1 && (
                                                                                <div
                                                                                    className={`absolute left-[11px] top-6 w-px h-[calc(100%-14px)] ${isDarkMode
                                                                                            ? 'bg-white/10'
                                                                                            : 'bg-black/10'
                                                                                        }`}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className={`py-4 text-center text-[9.5px] ${isDarkMode
                                                                            ? 'text-white/40'
                                                                            : 'text-black/40'
                                                                        }`}
                                                                >
                                                                    No tracking scans available
                                                                </div>
                                                            )}
                                                        </div>
                                                    </AccordionSection>

                                                    <AccordionSection
                                                        icon={FaBox}
                                                        title="Shipment Overview"
                                                        isOpen={openSection === 'overview'}
                                                        onToggle={() =>
                                                            handleToggleSection('overview')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="AWB Number"
                                                            value={detail?.AWB}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Order ID"
                                                            value={detail?.ReferenceNo}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Order Type"
                                                            value={detail?.OrderType}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Invoice Amount"
                                                            value={`₹${detail?.InvoiceAmount || 0}`}
                                                            icon={FaRupeeSign}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="COD Amount"
                                                            value={`₹${detail?.CODAmount || 0}`}
                                                            icon={FaRupeeSign}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Quantity"
                                                            value={detail?.Quantity}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    <AccordionSection
                                                        icon={FaMapMarkerAlt}
                                                        title="Location Details"
                                                        isOpen={openSection === 'location'}
                                                        onToggle={() =>
                                                            handleToggleSection('location')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Origin"
                                                            value={detail?.Origin}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Destination"
                                                            value={detail?.Destination}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Pickup Location"
                                                            value={detail?.PickupLocation}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    <AccordionSection
                                                        icon={FaUser}
                                                        title="Consignee Details"
                                                        isOpen={openSection === 'consignee'}
                                                        onToggle={() =>
                                                            handleToggleSection('consignee')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Name"
                                                            value={detail?.Consignee?.Name}
                                                            icon={FaUser}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="City"
                                                            value={
                                                                detail?.Consignee?.City ||
                                                                detail?.Consignee?.State
                                                            }
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Pin Code"
                                                            value={detail?.Consignee?.PinCode}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Country"
                                                            value={detail?.Consignee?.Country}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    <AccordionSection
                                                        icon={FaCalendarAlt}
                                                        title="Important Dates"
                                                        isOpen={openSection === 'dates'}
                                                        onToggle={() =>
                                                            handleToggleSection('dates')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Pickup Date"
                                                            value={formatDate(
                                                                detail?.PickUpDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Expected Delivery"
                                                            value={formatDate(
                                                                detail?.ExpectedDeliveryDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="First Attempt Date"
                                                            value={formatDate(
                                                                detail?.FirstAttemptDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>

                <div
                    className={`
            relative
            z-10

            px-5
            py-3.5

            border-t

            ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
          `}
                >
                    <button
                        onClick={handleViewAll}
                        className="
              w-full

              flex
              items-center
              justify-center
              gap-2

              py-2.5

              rounded-[14px]

              bg-red-600
              text-white

              text-[11px]
              font-bold

              shadow-[0_8px_25px_rgba(0,0,0,0.18)]

              transition-all
              duration-200

              hover:bg-black/85
              hover:-translate-y-[1px]

              active:translate-y-0
            "
                    >
                        <FaListUl size={11} />
                        View All Shipments
                    </button>
                </div>
            </div>
        </>
    );
};

export default B2BTrackingSidePopup;